import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceArea, CartesianGrid, ResponsiveContainer, Dot } from "recharts";
import { Thermometer } from "lucide-react";
import { api } from "@/lib/api";

const iso = (d) => d.toISOString().slice(0, 10);
const fmt = (s) => new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function TempDot(props) {
  const { cx, cy, payload } = props;
  return <Dot cx={cx} cy={cy} r={5} fill={payload.in_range ? "#059669" : "#DC2626"} stroke="#fff" strokeWidth={1.5} />;
}

function VehicleChart({ name, rows, ranges, idx }) {
  const data = rows.map((r) => ({ ...r, label: fmt(r.signed_at) }));
  const vals = rows.map((r) => r.temperatura);
  const min = Math.min(0, ...vals) - 1;
  const max = Math.max(30, ...vals) + 1;
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`temp-chart-${idx}`}>
      <p className="font-semibold mb-3 flex items-center gap-2"><Thermometer className="h-4 w-4 text-[#D32F2F]" /> {name}</p>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          {ranges.map(([lo, hi]) => <ReferenceArea key={lo} y1={lo} y2={hi} fill="#10B981" fillOpacity={0.12} />)}
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis domain={[Math.floor(min), Math.ceil(max)]} tick={{ fontSize: 11 }} unit="°" />
          <Tooltip formatter={(v) => [`${v} °C`, "Temperatura"]} />
          <Line type="monotone" dataKey="temperatura" stroke="#475569" strokeWidth={2} dot={<TempDot />} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function OutOfRangeTable({ rows }) {
  const bad = rows.filter((r) => !r.in_range || r.conservazione_ok === false);
  if (!bad.length) return <p className="text-sm text-emerald-700" data-testid="temp-all-ok">Nessun valore fuori norma nel periodo.</p>;
  return (
    <div className="rounded-xl border border-red-200 bg-card overflow-x-auto" data-testid="temp-out-table">
      <p className="bg-red-50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-red-700">Valori fuori norma / conservazione non corretta</p>
      <table className="w-full text-sm">
        <thead><tr className="text-xs text-muted-foreground text-left"><th className="px-4 py-2">Data</th><th className="px-4">Ambulanza</th><th className="px-4">Servizio</th><th className="px-4">Compilata da</th><th className="px-4">°C</th><th className="px-4">Conservazione</th></tr></thead>
        <tbody>
          {bad.map((r) => (
            <tr key={r.id} className="border-t border-border" data-testid={`temp-out-row-${r.id}`}>
              <td className="px-4 py-2">{new Date(r.signed_at).toLocaleString("it-IT")}</td>
              <td className="px-4">{r.vehicle_name || "Non indicata"}</td>
              <td className="px-4">{r.service_name}</td>
              <td className="px-4">{r.compiled_by_name}</td>
              <td className={`px-4 font-bold ${r.in_range ? "" : "text-red-700"}`}>{r.temperatura}</td>
              <td className={`px-4 font-semibold ${r.conservazione_ok ? "text-emerald-700" : "text-red-700"}`}>{r.conservazione_ok ? "SI" : "NO"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ChecklistTemperatures() {
  const [vehicles, setVehicles] = useState([]);
  const [f, setF] = useState(() => ({ vehicle_id: "", date_from: iso(new Date(Date.now() - 90 * 864e5)), date_to: iso(new Date()) }));
  const [data, setData] = useState({ ranges: [], rows: [] });

  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/checklists/temperatures", { params }).then((r) => setData(r.data)).catch(() => setData({ ranges: [], rows: [] }));
  }, [f]);
  const groups = useMemo(() => {
    const g = {};
    data.rows.forEach((r) => { const k = r.vehicle_name || "Ambulanza non indicata"; (g[k] = g[k] || []).push(r); });
    return Object.entries(g);
  }, [data]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-4" data-testid="temperatures-view">
      <div className="grid sm:grid-cols-4 gap-3 rounded-xl border border-border bg-card p-4">
        <div className="space-y-1">
          <Label>Ambulanza</Label>
          <Select value={f.vehicle_id || "all"} onValueChange={(v) => set("vehicle_id", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="temp-filter-vehicle"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.plate ? ` (${v.plate})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label>Dal</Label><Input type="date" data-testid="temp-date-from" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} /></div>
        <div className="space-y-1"><Label>Al</Label><Input type="date" data-testid="temp-date-to" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} /></div>
        <p className="text-xs text-muted-foreground self-end">A norma: {data.ranges.map(([lo, hi]) => `${lo}–${hi} °C`).join(" oppure ")} (fasce verdi)</p>
      </div>
      {groups.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="temp-empty">Nessuna temperatura registrata nel periodo.</p>
      ) : (
        <>
          <div className="grid lg:grid-cols-2 gap-4">{groups.map(([name, rows], i) => <VehicleChart key={name} name={name} rows={rows} ranges={data.ranges} idx={i} />)}</div>
          <OutOfRangeTable rows={data.rows} />
        </>
      )}
    </div>
  );
}
